import { useCallback } from 'react';
import { AlertCircle } from 'lucide-react';

interface StackErrorPanelProps {
  errors: string[];
  /** Heading shown above the list. */
  title?: string;
  /** Server failures are announced and focused when they appear; client validation is not. */
  alert?: boolean;
}

export function StackErrorPanel({ errors, title = 'Validation errors', alert = false }: StackErrorPanelProps) {
  // Stable callback ref: focuses the panel when it mounts, never on unrelated re-renders.
  const focusOnMount = useCallback((el: HTMLDivElement | null) => {
    if (alert) el?.focus();
  }, [alert]);

  if (errors.length === 0) return null;

  return (
    <div
      ref={focusOnMount}
      role={alert ? 'alert' : undefined}
      tabIndex={alert ? -1 : undefined}
      className="rounded-md border border-destructive/20 bg-destructive/10 p-3 outline-none"
    >
      <div className="mb-2 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
        <span className="font-medium text-destructive">{title}</span>
      </div>
      <ul className="list-inside list-disc space-y-1 text-sm text-destructive [overflow-wrap:anywhere]">
        {errors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    </div>
  );
}
